import type { Feature, FeatureInfo } from '@/services/recruiterApi';

interface Props {
  /** Feature catalog to render (server list when available, else static fallback). */
  features: readonly FeatureInfo[];
  /** Currently-selected feature keys. */
  selected: readonly Feature[];
  onToggle: (key: Feature) => void;
}

/**
 * The plan/trial "what's included" picker: one labelled toggle switch per
 * feature. Each switch is a real `<input type="checkbox">` (kept accessible via
 * `aria-label`) visually restyled as a slider — we intentionally replace the
 * native glyph here, unlike a plain checkbox, so the input is layered invisibly
 * over the drawn track rather than inheriting text-input styling.
 */
export function FeatureToggles({ features, selected, onToggle }: Props): JSX.Element {
  return (
    <div className="feature-toggles" role="group">
      {features.map((f) => {
        const on = selected.includes(f.key);
        return (
          <label key={f.key} className="feature-toggle" data-on={on}>
            <span className="feature-toggle__text">
              <span className="feature-toggle__name">{f.label}</span>
              <span className="feature-toggle__desc">{f.description}</span>
            </span>
            <input
              type="checkbox"
              className="feature-toggle__input"
              checked={on}
              onChange={() => onToggle(f.key)}
              aria-label={f.label}
            />
            <span className="feature-toggle__switch" aria-hidden="true" />
          </label>
        );
      })}
    </div>
  );
}
